import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

// ── Keep localStorage keys for color preset (UI only, no Supabase) ──
const SETTINGS_KEY = 'ebdaa_store_settings_v2';

export interface DiscountCode {
  id?: number;
  code: string;
  percent: number;
  active: boolean;
  usageCount: number;
  createdAt: string;
}

export interface ColorPreset {
  id: string;
  name: string;
  primary: string;
  primaryLight: string;
  primaryDark: string;
  textOnPrimary: string;
}

export const COLOR_PRESETS: ColorPreset[] = [
  { id: 'gold',    name: 'ذهبي',    primary: '#fbbf24', primaryLight: '#fde68a', primaryDark: '#f59e0b', textOnPrimary: '#000' },
  { id: 'rose',    name: 'وردي',    primary: '#f43f5e', primaryLight: '#fda4af', primaryDark: '#e11d48', textOnPrimary: '#fff' },
  { id: 'emerald', name: 'زمردي',   primary: '#10b981', primaryLight: '#6ee7b7', primaryDark: '#059669', textOnPrimary: '#fff' },
  { id: 'sky',     name: 'سماوي',   primary: '#38bdf8', primaryLight: '#bae6fd', primaryDark: '#0ea5e9', textOnPrimary: '#000' },
  { id: 'violet',  name: 'بنفسجي',  primary: '#a78bfa', primaryLight: '#ddd6fe', primaryDark: '#7c3aed', textOnPrimary: '#fff' },
  { id: 'orange',  name: 'برتقالي', primary: '#fb923c', primaryLight: '#fed7aa', primaryDark: '#ea580c', textOnPrimary: '#fff' },
  { id: 'teal',    name: 'فيروزي',  primary: '#2dd4bf', primaryLight: '#99f6e4', primaryDark: '#0d9488', textOnPrimary: '#000' },
  { id: 'red',     name: 'أحمر',    primary: '#ef4444', primaryLight: '#fca5a5', primaryDark: '#dc2626', textOnPrimary: '#fff' },
  { id: 'lime',    name: 'ليموني',  primary: '#a3e635', primaryLight: '#d9f99d', primaryDark: '#65a30d', textOnPrimary: '#000' },
  { id: 'white',   name: 'أبيض',    primary: '#f8fafc', primaryLight: '#ffffff', primaryDark: '#e2e8f0', textOnPrimary: '#000' },
];

export function applyColorPreset(preset: ColorPreset) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', preset.primary);
  root.style.setProperty('--color-primary-light', preset.primaryLight);
  root.style.setProperty('--color-primary-dark', preset.primaryDark);
  root.style.setProperty('--color-text-on-primary', preset.textOnPrimary);
}

export interface BusinessHourEntry {
  open: string;
  close: string;
  closed: boolean;
}

export interface BusinessHours {
  saturday:  BusinessHourEntry;
  sunday:    BusinessHourEntry;
  monday:    BusinessHourEntry;
  tuesday:   BusinessHourEntry;
  wednesday: BusinessHourEntry;
  thursday:  BusinessHourEntry;
  friday:    BusinessHourEntry;
}

export const DAY_LABELS: Record<keyof BusinessHours, string> = {
  saturday: "السبت", sunday: "الأحد", monday: "الاثنين",
  tuesday: "الثلاثاء", wednesday: "الأربعاء", thursday: "الخميس", friday: "الجمعة",
};

export const DAY_ORDER: (keyof BusinessHours)[] = [
  "saturday", "sunday", "monday", "tuesday", "wednesday", "thursday", "friday",
];

export const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  saturday:  { open: "09:00", close: "22:00", closed: false },
  sunday:    { open: "09:00", close: "22:00", closed: false },
  monday:    { open: "09:00", close: "22:00", closed: false },
  tuesday:   { open: "09:00", close: "22:00", closed: false },
  wednesday: { open: "09:00", close: "22:00", closed: false },
  thursday:  { open: "09:00", close: "22:00", closed: false },
  friday:    { open: "14:00", close: "22:00", closed: false },
};

export interface StoreSettings {
  storeName: string;
  adminWhatsApp: string;
  announcementText: string;
  announcementEnabled: boolean;
  paymentCashEnabled: boolean;
  paymentBankEnabled: boolean;
  bankName: string;
  bankAccountName: string;
  bankAccountNumber: string;
  bankIban: string;
  logoUrl: string;
  logoText: string;
  logoSubText: string;
  colorPresetId: string;
  businessHours: BusinessHours;
}

export const DEFAULT_SETTINGS: StoreSettings = {
  storeName: 'متجر الإبداع',
  adminWhatsApp: '966541138847',
  announcementText: 'شحن مجاني لجميع الطلبات داخل المملكة',
  announcementEnabled: true,
  paymentCashEnabled: true,
  paymentBankEnabled: true,
  bankName: '',
  bankAccountName: '',
  bankAccountNumber: '',
  bankIban: '',
  logoUrl: '',
  logoText: 'إبداع',
  logoSubText: 'AL EBDAA',
  colorPresetId: 'gold',
  businessHours: DEFAULT_BUSINESS_HOURS,
};

const DEFAULT_CITIES = [
  'الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام', 'الخبر',
  'الظهران', 'الطائف', 'بريدة', 'القطيف', 'الجبيل', 'أبها',
  'نجران', 'تبوك', 'ينبع', 'حائل', 'الأحساء', 'الخرج',
  'القصيم', 'عرعر', 'سكاكا', 'جازان', 'شرورة',
];

const DEFAULT_BADGES = ['جديد', 'الأكثر مبيعاً', 'عرض محدود', 'حصري', 'خصم', 'نفاد قريب'];

function rowToSettings(row: Record<string, unknown>): StoreSettings {
  return {
    storeName: String(row.store_name ?? DEFAULT_SETTINGS.storeName),
    adminWhatsApp: String(row.admin_whatsapp ?? DEFAULT_SETTINGS.adminWhatsApp),
    announcementText: String(row.announcement_text ?? DEFAULT_SETTINGS.announcementText),
    announcementEnabled: Boolean(row.announcement_enabled ?? true),
    paymentCashEnabled: Boolean(row.payment_cash_enabled ?? true),
    paymentBankEnabled: Boolean(row.payment_bank_enabled ?? true),
    bankName: String(row.bank_name ?? ''),
    bankAccountName: String(row.bank_account_name ?? ''),
    bankAccountNumber: String(row.bank_account_number ?? ''),
    bankIban: String(row.bank_iban ?? ''),
    logoUrl: String(row.logo_url ?? ''),
    logoText: String(row.logo_text ?? 'إبداع'),
    logoSubText: String(row.logo_sub_text ?? 'AL EBDAA'),
    colorPresetId: String(row.color_preset_id ?? 'gold'),
    businessHours: {
      ...DEFAULT_BUSINESS_HOURS,
      ...((row.business_hours as BusinessHours) ?? {}),
    },
  };
}

// ── Store Settings ──────────────────────────────────────────
export function useStoreSettings() {
  const [settings, setSettings] = useState<StoreSettings>(() => {
    // Load from localStorage as initial fast-load fallback
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (raw) {
        const s = JSON.parse(raw) as StoreSettings;
        return { ...DEFAULT_SETTINGS, ...s, businessHours: { ...DEFAULT_BUSINESS_HOURS, ...(s.businessHours ?? {}) } };
      }
    } catch { /* ignore */ }
    return DEFAULT_SETTINGS;
  });

  // Apply color on mount
  useEffect(() => {
    const preset = COLOR_PRESETS.find(p => p.id === settings.colorPresetId) ?? COLOR_PRESETS[0];
    applyColorPreset(preset);
  }, [settings.colorPresetId]);

  // Fetch from Supabase on mount
  useEffect(() => {
    const fetch = async () => {
      const { data, error } = await supabase
        .from('store_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();
      if (!error && data) {
        const s = rowToSettings(data as Record<string, unknown>);
        setSettings(s);
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
        const preset = COLOR_PRESETS.find(p => p.id === s.colorPresetId) ?? COLOR_PRESETS[0];
        applyColorPreset(preset);
      }
    };
    fetch();
  }, []);

  const updateSettings = useCallback(async (patch: Partial<StoreSettings>) => {
    setSettings(prev => {
      const updated = { ...prev, ...patch };
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
      if (patch.colorPresetId) {
        const preset = COLOR_PRESETS.find(p => p.id === patch.colorPresetId) ?? COLOR_PRESETS[0];
        applyColorPreset(preset);
      }
      return updated;
    });

    // Persist to Supabase
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (patch.storeName !== undefined) payload.store_name = patch.storeName;
    if (patch.adminWhatsApp !== undefined) payload.admin_whatsapp = patch.adminWhatsApp;
    if (patch.announcementText !== undefined) payload.announcement_text = patch.announcementText;
    if (patch.announcementEnabled !== undefined) payload.announcement_enabled = patch.announcementEnabled;
    if (patch.paymentCashEnabled !== undefined) payload.payment_cash_enabled = patch.paymentCashEnabled;
    if (patch.paymentBankEnabled !== undefined) payload.payment_bank_enabled = patch.paymentBankEnabled;
    if (patch.bankName !== undefined) payload.bank_name = patch.bankName;
    if (patch.bankAccountName !== undefined) payload.bank_account_name = patch.bankAccountName;
    if (patch.bankAccountNumber !== undefined) payload.bank_account_number = patch.bankAccountNumber;
    if (patch.bankIban !== undefined) payload.bank_iban = patch.bankIban;
    if (patch.logoUrl !== undefined) payload.logo_url = patch.logoUrl;
    if (patch.logoText !== undefined) payload.logo_text = patch.logoText;
    if (patch.logoSubText !== undefined) payload.logo_sub_text = patch.logoSubText;
    if (patch.colorPresetId !== undefined) payload.color_preset_id = patch.colorPresetId;
    if (patch.businessHours !== undefined) payload.business_hours = patch.businessHours;

    await supabase
      .from('store_settings')
      .upsert({ id: 1, ...payload });
  }, []);

  const resetSettings = useCallback(async () => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(DEFAULT_SETTINGS));
    applyColorPreset(COLOR_PRESETS[0]);
    await supabase.from('store_settings').upsert({
      id: 1,
      store_name: DEFAULT_SETTINGS.storeName,
      admin_whatsapp: DEFAULT_SETTINGS.adminWhatsApp,
      announcement_text: DEFAULT_SETTINGS.announcementText,
      announcement_enabled: true,
      payment_cash_enabled: true,
      payment_bank_enabled: true,
      bank_name: '', bank_account_name: '', bank_account_number: '', bank_iban: '',
      logo_url: '', logo_text: 'إبداع', logo_sub_text: 'AL EBDAA',
      color_preset_id: 'gold',
      business_hours: DEFAULT_BUSINESS_HOURS,
      updated_at: new Date().toISOString(),
    });
  }, []);

  return { settings, updateSettings, resetSettings };
}

// ── Discount Codes ──────────────────────────────────────────
export function useDiscountCodes() {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('discount_codes')
      .select('*')
      .order('created_at', { ascending: true });
    if (!error && data) {
      setCodes(data.map(r => ({
        id: r.id,
        code: r.code,
        percent: r.percent,
        active: r.active,
        usageCount: r.usage_count ?? 0,
        createdAt: r.created_at,
      })));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const addCode = useCallback(async (code: string, percent: number) => {
    const { data, error } = await supabase
      .from('discount_codes')
      .insert({ code: code.trim().toUpperCase(), percent, active: true, usage_count: 0 })
      .select()
      .maybeSingle();
    if (!error && data) {
      setCodes(prev => [...prev, {
        id: data.id, code: data.code, percent: data.percent,
        active: data.active, usageCount: data.usage_count ?? 0, createdAt: data.created_at,
      }]);
    }
  }, []);

  const toggleCode = useCallback(async (code: string) => {
    const existing = codes.find(c => c.code === code);
    if (!existing) return;
    const newActive = !existing.active;
    await supabase.from('discount_codes').update({ active: newActive }).eq('code', code);
    setCodes(prev => prev.map(c => c.code === code ? { ...c, active: newActive } : c));
  }, [codes]);

  const deleteCode = useCallback(async (code: string) => {
    await supabase.from('discount_codes').delete().eq('code', code);
    setCodes(prev => prev.filter(c => c.code !== code));
  }, []);

  const validateCode = useCallback((input: string): DiscountCode | null => {
    return codes.find(c => c.code.toLowerCase() === input.trim().toLowerCase() && c.active) ?? null;
  }, [codes]);

  const incrementUsage = useCallback(async (code: string) => {
    const existing = codes.find(c => c.code.toLowerCase() === code.toLowerCase());
    if (!existing) return;
    const newCount = (existing.usageCount ?? 0) + 1;
    await supabase.from('discount_codes').update({ usage_count: newCount }).eq('code', code);
    setCodes(prev => prev.map(c => c.code.toLowerCase() === code.toLowerCase()
      ? { ...c, usageCount: newCount } : c));
  }, [codes]);

  return { codes, loading, addCode, toggleCode, deleteCode, validateCode, incrementUsage };
}

// ── Cities ──────────────────────────────────────────────────
export function useCities() {
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCities = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('cities')
      .select('name')
      .order('sort_order', { ascending: true });
    if (!error && data && data.length > 0) {
      setCities(data.map(r => r.name));
    } else {
      // Fallback to defaults if table is empty
      setCities(DEFAULT_CITIES);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCities(); }, [fetchCities]);

  const addCity = useCallback(async (city: string) => {
    const trimmed = city.trim();
    if (!trimmed || cities.includes(trimmed)) return;
    const { error } = await supabase.from('cities').insert({ name: trimmed, sort_order: cities.length + 1 });
    if (!error) setCities(prev => [...prev, trimmed]);
  }, [cities]);

  const removeCity = useCallback(async (city: string) => {
    await supabase.from('cities').delete().eq('name', city);
    setCities(prev => prev.filter(c => c !== city));
  }, []);

  const resetCities = useCallback(async () => {
    // Delete all and re-insert defaults
    await supabase.from('cities').delete().neq('id', 0);
    const rows = DEFAULT_CITIES.map((name, i) => ({ name, sort_order: i + 1 }));
    await supabase.from('cities').insert(rows);
    setCities(DEFAULT_CITIES);
  }, []);

  return { cities, loading, addCity, removeCity, resetCities };
}

// ── Badges ──────────────────────────────────────────────────
export function useBadges() {
  const [badges, setBadges] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBadges = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('badges')
      .select('name')
      .order('sort_order', { ascending: true });
    if (!error && data && data.length > 0) {
      setBadges(data.map(r => r.name));
    } else {
      setBadges(DEFAULT_BADGES);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  const addBadge = useCallback(async (badge: string) => {
    const trimmed = badge.trim();
    if (!trimmed || badges.includes(trimmed)) return;
    const { error } = await supabase.from('badges').insert({ name: trimmed, sort_order: badges.length + 1 });
    if (!error) setBadges(prev => [...prev, trimmed]);
  }, [badges]);

  const removeBadge = useCallback(async (badge: string) => {
    await supabase.from('badges').delete().eq('name', badge);
    setBadges(prev => prev.filter(b => b !== badge));
  }, []);

  const resetBadges = useCallback(async () => {
    await supabase.from('badges').delete().neq('id', 0);
    const rows = DEFAULT_BADGES.map((name, i) => ({ name, sort_order: i + 1 }));
    await supabase.from('badges').insert(rows);
    setBadges(DEFAULT_BADGES);
  }, []);

  return { badges, loading, addBadge, removeBadge, resetBadges };
}
